import React, { useCallback, useMemo, useRef, useState } from 'react';
import { client } from '../../utils/trpc-client';
import { formatMoney } from 'shared/src/utils/money/money-utils';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-quartz.css';
import {
  AdvancedFilterModel,
  ColDef,
  FilterModel,
  GridApi,
  GridReadyEvent,
  SortModelItem,
  StatusPanelDef,
  ValueFormatterParams
} from '@ag-grid-community/core';
import { AgGridReact, CustomCellRendererProps } from '@ag-grid-community/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { PageTitle } from '../shared/page-title';
import {
  PlatformBuysFilterTypes,
  PlatformBuySort,
  PlatformBuySortColumn,
  PlatformBuySortColumns,
  PlatformBuyTableRow
} from 'shared/src/domain/media-buy/media-buy-types';
import { SearchRow } from '../../components/display/search-bar';
import { showErrorToast } from '../../components/feedback/error-toast';
import {
  getPlatformEntityType,
  getPlatformName
} from 'shared/src/domain/media-platform/media-platforms';
import { AllPlatformBuysIcon } from '../../components/layout/nav-icons';
import { TotalStatusPanel } from '../shared/totals-status-panel';
import { useCaptureListingPageView } from '../../utils/posthog/analytics';
import { defaultColDef } from '../../components/tables/table-utils';
import { parseErrorMsg } from '../../utils/listing-utils';
import { useUrlTableState } from '../../components/tables/table-state-persistence';
import { parseFilterModelPerKey } from 'shared/src/utils/filter/parse-filter-model';
import { useChannelRatesContext } from '@/features/strategy/channel-rates-context';

export function AllPlatformBuys() {
  const gridApi = useRef<GridApi<PlatformBuyTableRow>>(undefined);
  const { platforms } = useChannelRatesContext();
  const { initialState, storeGridState, storeSearch, getSearch } = useUrlTableState();
  const [columnDefs] = useState<ColDef<PlatformBuyTableRow>[]>([
    {
      field: 'id',
      cellRenderer: (props: CustomCellRendererProps) => {
        if (props.value !== undefined) {
          return props.value;
        } else {
          return <FontAwesomeIcon icon={faSpinner} className="animate-rotate text-blue-500" />;
        }
      }
    },
    {
      field: 'name',
      headerName: 'Name'
    },
    {
      field: 'media_platform_id',
      headerName: 'Platform Entity',
      filter: 'agSetColumnFilter',
      filterParams: {
        values: platforms.map(p => p.id),
        valueFormatter: (prop: ValueFormatterParams<PlatformBuyTableRow>) =>
          `${getPlatformName(platforms, prop.value)} ${getPlatformEntityType(prop.value)}`
      },
      valueFormatter: prop =>
        `${getPlatformName(platforms, prop.value)} ${getPlatformEntityType(prop.value)}`
    },
    {
      field: 'budget',
      headerName: 'Platform Budget',
      valueFormatter: ({ value }) => (value ? formatMoney(value) : ''),
      filter: 'agNumberColumnFilter',
      filterParams: {
        allowedCharPattern: '\\d\\-\\,',
        filterOptions: ['equals', 'greaterThan', 'lessThan', 'inRange'],
        maxNumConditions: 1
      }
    },
    {
      field: 'spend',
      headerName: 'Spend',
      valueFormatter: ({ value }) => (value ? formatMoney(value) : ''),
      filter: 'agNumberColumnFilter',
      filterParams: {
        allowedCharPattern: '\\d\\-\\,',
        filterOptions: ['equals', 'greaterThan', 'lessThan', 'inRange'],
        maxNumConditions: 1
      }
    },
    {
      field: 'start_date',
      headerName: 'Start Date',
      filter: 'agDateColumnFilter',
      filterParams: {
        buttons: ['apply', 'reset'],
        filterOptions: ['equals', 'lessThan', 'greaterThan', 'inRange'],
        maxNumConditions: 1
      }
    },
    {
      field: 'end_date',
      headerName: 'End Date',
      filter: 'agDateColumnFilter',
      filterParams: {
        buttons: ['apply', 'reset'],
        filterOptions: ['equals', 'lessThan', 'greaterThan', 'inRange'],
        maxNumConditions: 1
      }
    },
    {
      field: 'num_line_items',
      headerName: 'Linked Line Items',
      filter: 'agNumberColumnFilter',
      filterParams: {
        allowedCharPattern: '\\d\\-\\,',
        filterOptions: ['equals', 'greaterThan', 'lessThan', 'inRange'],
        maxNumConditions: 1
      }
    },
    {
      field: 'campaign_numbers',
      headerName: 'Linked Campaigns',
      valueFormatter: ({ value }) => (value ? value.length : 0),
      filter: 'agNumberColumnFilter',
      filterParams: {
        allowedCharPattern: '\\d\\-\\,',
        filterOptions: ['equals', 'greaterThan', 'lessThan', 'inRange'],
        maxNumConditions: 1
      }
    },
    {
      field: 'updated_at',
      headerName: 'Last Synced'
    }
  ]);

  useCaptureListingPageView('platform_buy');

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      gridApi.current = params.api;
      gridApi.current.setGridOption('serverSideDatasource', {
        getRows: async params => {
          try {
            const filters = parseFilterModel(params.request.filterModel);
            const page = params.request.endRow ? Math.ceil(params.request.endRow / 50) : 1;
            const sort = getCurrentSort(params.request.sortModel);
            const requestParams = { page, sort, filters, search: getSearch() };
            const { media_buys, total } = await client.platformBuys.query(requestParams);
            params.success({ rowData: media_buys, rowCount: total });
          } catch (error) {
            console.error('Failed to fetch platform buys', error);
            showErrorToast(parseErrorMsg(error));
            return;
          }
        }
      });
    },
    [getSearch]
  );

  const statusBar = useMemo<{ statusPanels: StatusPanelDef[] }>(
    () => ({ statusPanels: [{ statusPanel: TotalStatusPanel, align: 'left' }] }),
    []
  );

  function setSearchText(search: string) {
    storeSearch(search);
    gridApi.current?.refreshServerSide({ purge: true });
  }

  return (
    <div className="flex h-full w-full flex-col px-8 pb-8 pt-3">
      <PageTitle title="Bravo | Platform Buys" />
      <SearchRow
        icon={<AllPlatformBuysIcon />}
        title="Platform Buys"
        defaultQuery={getSearch()}
        placeholder="Search By Platform Buy"
        submitSearch={setSearchText}
      />
      <div className="ag-theme-quartz" style={{ height: '100%', width: '100%' }}>
        <AgGridReact
          columnDefs={columnDefs}
          defaultColDef={{
            ...defaultColDef,
            suppressHeaderMenuButton: false,
            flex: 1,
            minWidth: 100
          }}
          rowBuffer={0}
          rowModelType={'serverSide'}
          cacheBlockSize={50}
          cacheOverflowSize={2}
          isExternalFilterPresent={() => true}
          infiniteInitialRowCount={100}
          maxBlocksInCache={10}
          sideBar={{ toolPanels: ['filters'] }}
          onGridReady={onGridReady}
          statusBar={statusBar}
          initialState={initialState}
          onStateUpdated={storeGridState}
        />
      </div>
    </div>
  );
}

function getCurrentSort(sortModel: SortModelItem[]): PlatformBuySort | undefined {
  if (sortModel.length === 0) return undefined;
  const { sort, colId } = sortModel[0];
  if (PlatformBuySortColumns.includes(colId)) {
    // TODO[mk]: properly refine type
    return { column: colId as PlatformBuySortColumn, direction: sort };
  } else return undefined;
}

function parseFilterModel(filterModel: FilterModel | AdvancedFilterModel | null) {
  if (!filterModel || 'filterType' in filterModel) return {};
  return parseFilterModelPerKey(PlatformBuysFilterTypes, filterModel);
}
